/**
 * RadioTeaserButton
 *
 * Convite flutuante na landing page: um botão pill "🎵 Ouça a rádio dos compositores"
 * que, ao ser clicado, carrega a playlist pública e delega o playback ao
 * GlobalRadioPlayer (via RadioPlayerContext).
 *
 * Comportamento:
 * - Aparece SOMENTE quando não há música tocando (currentTrack === null)
 * - Uma vez iniciada a rádio, some (GlobalRadioPlayer assume)
 * - Mostra prévia da próxima música (título + autor) antes do usuário clicar
 *   para dar contexto do que ele vai ouvir
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Play, Radio, X } from 'lucide-react';
import { useRadioPlayer } from '../contexts/RadioPlayerContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const DISMISS_KEY = 'mmr_radio_teaser_dismissed';

// Rotas onde o teaser DEVE aparecer (público, home, galeria).
// Fora daqui o botão fica escondido para não competir com CTAs.
const ALLOWED_PATHS = ['/', '/galeria', '/gallery/lyrics', '/radio'];

export default function RadioTeaserButton() {
  const { pathname } = useLocation();
  const { currentTrack, setPlaylistAndPlay } = useRadioPlayer();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Só renderiza em rotas públicas listadas
  const inAllowedRoute = useMemo(
    () => ALLOWED_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/')),
    [pathname]
  );

  // Buscar uma prévia da próxima música para dar contexto ao visitante
  useEffect(() => {
    if (!inAllowedRoute || dismissed || currentTrack) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${BACKEND_URL}/api/gallery/radio-playlist`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return;
        const data = await res.json();
        const publicas = Array.isArray(data?.publicas) ? data.publicas : [];
        if (publicas.length === 0) return;
        // Escolhe uma aleatória para prévia (efeito "wow" na home)
        const random = publicas[Math.floor(Math.random() * publicas.length)];
        if (!cancelled) setPreview(random);
      } catch {
        /* silencioso — teaser é opcional */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inAllowedRoute, dismissed, currentTrack]);

  const handleStart = async () => {
    try {
      setStarting(true);
      const res = await fetch(`${BACKEND_URL}/api/gallery/radio-playlist`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`radio HTTP ${res.status}`);
      const data = await res.json();
      // Ordem: tenta ordem_execucao (destaque+publicas alternado), fallback publicas
      const source = Array.isArray(data?.ordem_execucao) && data.ordem_execucao.length
        ? data.ordem_execucao
        : (data?.publicas || []);
      if (source.length === 0) return;

      const tracks = source
        .filter((t) => t && t.id && t.audio_file_id)
        .map((t) => ({
          id: t.id,
          title: t.title || 'Sem título',
          author: t.author_name || 'Compositor',
          artist: t.author_name || 'Compositor',
          genre: t.genre || '',
          audioUrl: `${BACKEND_URL}/api/gallery/audio/${t.id}`,
          coverUrl: t.cover_image_id
            ? `${BACKEND_URL}/api/gallery/cover/${t.id}`
            : null,
        }));

      if (tracks.length === 0) return;
      // Se o preview mostrado ao usuário está na lista, começa dele para dar coerência
      const startIndex = preview
        ? Math.max(0, tracks.findIndex((t) => t.id === preview.id))
        : 0;
      setPlaylistAndPlay(tracks, startIndex);
    } catch (err) {
      console.warn('[RadioTeaser] falha ao iniciar rádio:', err);
    } finally {
      setStarting(false);
    }
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch { /* noop */ }
    setDismissed(true);
  };

  // Regras de exibição
  if (currentTrack) return null;
  if (!inAllowedRoute) return null;
  if (dismissed) return null;

  return (
    <div
      className="fixed z-40 left-4 bottom-24 md:left-6 md:bottom-6 max-w-[92vw] pointer-events-auto"
      data-testid="radio-teaser-container"
    >
      <button
        onClick={handleStart}
        disabled={starting}
        aria-label="Iniciar Rádio dos Compositores"
        data-testid="radio-teaser-start-btn"
        className="group relative flex items-center gap-3 pl-3 pr-4 py-2.5 md:pl-4 md:pr-5 md:py-3
                   rounded-full shadow-2xl
                   bg-gradient-to-r from-purple-700 via-pink-600 to-orange-500
                   text-white
                   hover:scale-[1.03] active:scale-[0.98]
                   transition-all duration-200
                   ring-2 ring-white/20 hover:ring-white/40
                   backdrop-blur-sm
                   before:absolute before:inset-0 before:rounded-full before:animate-ping
                   before:bg-purple-500/20 before:pointer-events-none"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        {/* Ícone com pulsação */}
        <span className="relative flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/15">
          {starting ? (
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Play className="w-5 h-5 fill-white" />
          )}
          <span className="absolute inset-0 rounded-full ring-2 ring-white/40 animate-pulse" />
        </span>

        {/* Texto */}
        <span className="flex flex-col text-left leading-tight">
          <span className="flex items-center gap-1.5 text-[10px] md:text-xs font-bold uppercase tracking-wider text-white/85">
            <Radio className="w-3 h-3" />
            <span>Rádio ao vivo</span>
            <span className="ml-1 flex items-center gap-0.5" aria-hidden>
              <span className="w-0.5 h-2 bg-white/90 rounded animate-[eq_1s_ease-in-out_infinite]" />
              <span className="w-0.5 h-3 bg-white/90 rounded animate-[eq_0.8s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} />
              <span className="w-0.5 h-1.5 bg-white/90 rounded animate-[eq_1.2s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} />
            </span>
          </span>
          <span className="text-sm md:text-base font-semibold max-w-[42vw] md:max-w-[280px] truncate">
            {loading && !preview
              ? 'Carregando obras…'
              : preview
                ? `Ouvir "${preview.title}"`
                : 'Ouça obras dos compositores'}
          </span>
          {preview && (
            <span className="text-[10px] md:text-xs text-white/75 truncate max-w-[42vw] md:max-w-[280px]">
              por {preview.author_name}{preview.genre ? ` · ${preview.genre}` : ''}
            </span>
          )}
        </span>
      </button>

      {/* Botão de fechar (dismiss por sessão) */}
      <button
        onClick={handleDismiss}
        aria-label="Fechar convite da rádio"
        data-testid="radio-teaser-dismiss-btn"
        className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-black/70 hover:bg-black
                   text-white flex items-center justify-center border border-white/20 shadow-lg"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      {/* Keyframes locais (equalizer) */}
      <style>{`
        @keyframes eq {
          0%, 100% { transform: scaleY(0.4); }
          50%      { transform: scaleY(1); }
        }
      `}</style>
    </div>
  );
}
